import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactECharts from "echarts-for-react";
import { getAgentArtifactJson, getAgentArtifactUrl } from "../../api/Agent";
import { buildOption, dataArtifactToCsv, pairChartArtifacts } from "../../helpers/agentChart";

export default function AgentArtifacts({ artifacts, sessionId, getAccessToken }) {
    const { pairs, csvFiles } = pairChartArtifacts(artifacts || []);

    if ((!pairs || pairs.length === 0) && (!csvFiles || csvFiles.length === 0)) {
        return null;
    }

    return (
        <div className="mt-3 flex flex-col gap-3">
            {pairs.map((pair) => (
                <AgentChart
                    key={`${pair.chart.name}-${pair.chart.version}`}
                    pair={pair}
                    sessionId={sessionId}
                    getAccessToken={getAccessToken}
                />
            ))}
            {csvFiles.map((csv) => (
                <AgentCsvLink
                    key={`${csv.name}-${csv.version}`}
                    artifact={csv}
                    sessionId={sessionId}
                    getAccessToken={getAccessToken}
                />
            ))}
        </div>
    );
}

function AgentChart({ pair, sessionId, getAccessToken }) {
    const { t } = useTranslation();
    const [option, setOption] = useState(null);
    const [csvText, setCsvText] = useState("");
    const [error, setError] = useState(false);
    const [chartHeight, setChartHeight] = useState(() =>
        typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
            ? 220
            : 320
    );
    const chartName = pair.chart?.name;
    const chartVersion = pair.chart?.version;
    const dataName = pair.data?.name;
    const dataVersion = pair.data?.version;

    useEffect(() => {
        const media = window.matchMedia("(max-width: 767px)");
        const apply = () => setChartHeight(media.matches ? 220 : 320);
        apply();
        media.addEventListener("change", apply);
        return () => media.removeEventListener("change", apply);
    }, []);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!chartName || !sessionId) {
                return;
            }
            if (!dataName) {
                setError(true);
                return;
            }
            setError(false);
            let lastError;
            for (let attempt = 0; attempt < 4; attempt += 1) {
                if (cancelled) {
                    return;
                }
                try {
                    const token = await getAccessToken();
                    const [chartConfig, dataArtifact] = await Promise.all([
                        getAgentArtifactJson(token, sessionId, chartName, chartVersion),
                        getAgentArtifactJson(token, sessionId, dataName, dataVersion),
                    ]);
                    if (!cancelled) {
                        setOption(buildOption(chartConfig, dataArtifact));
                        setCsvText(dataArtifactToCsv(dataArtifact));
                        setError(false);
                    }
                    return;
                } catch (error) {
                    lastError = error;
                    await new Promise((resolve) => {
                        setTimeout(resolve, 400 * (attempt + 1));
                    });
                }
            }
            if (!cancelled) {
                console.error("Could not load chart artifact", lastError);
                setError(true);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [getAccessToken, chartName, chartVersion, dataName, dataVersion, sessionId]);

    if (error || !pair.data) {
        return <p className="text-xs text-error-600">{t("agent.couldNotLoadChart")}</p>;
    }

    if (!option) {
        return (
            <div
                className="animate-pulse rounded-xl bg-mute-50"
                style={{ height: chartHeight }}
            />
        );
    }

    const fileName = `${String(pair.chart.name || "chart")
        .replace(/_(chart|config)\.json$/i, "")
        .replace(/[^\w\-]+/g, "_") || "chart"}.csv`;

    return (
        <div className="flex min-w-0 flex-col gap-2 overflow-x-auto">
            <ReactECharts
                option={option}
                style={{ height: chartHeight, width: "100%" }}
                notMerge
                lazyUpdate
            />
            {pair.csv ? (
                <AgentCsvLink
                    artifact={pair.csv}
                    sessionId={sessionId}
                    getAccessToken={getAccessToken}
                />
            ) : csvText ? (
                <button
                    type="button"
                    className="btn-sm btn-outline-primary w-fit"
                    onClick={() => downloadCsv(fileName, csvText)}
                >
                    {t("agent.downloadCsv")}
                </button>
            ) : null}
        </div>
    );
}

function downloadCsv(fileName, csvText) {
    const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    window.URL.revokeObjectURL(url);
}

function AgentCsvLink({ artifact, sessionId, getAccessToken }) {
    const { t } = useTranslation();
    const [url, setUrl] = useState("");

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const token = await getAccessToken();
                const result = await getAgentArtifactUrl(token, sessionId, artifact.name, artifact.version);
                if (!cancelled) {
                    setUrl(result.url);
                }
            } catch {
                if (!cancelled) {
                    setUrl("");
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [artifact.name, artifact.version, getAccessToken, sessionId]);

    if (!url) {
        return <span className="text-xs text-mute-500">{t("agent.preparingCsv")}</span>;
    }

    return (
        <a className="btn-sm btn-outline-primary w-fit" href={url} target="_blank" rel="noreferrer">
            {t("agent.downloadCsv")}
        </a>
    );
}
